import { Badge, CircleQuestionIcon, InfoIcon, Stack } from '@fukuri-admins/ui/common';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@fukuri-admins/ui/common/Form/FormItems';
import stressCheckReportTemplate from '~/assets/images/stress_check_report_template.png';
import styles from './styles.module.scss';

const OCCUPATIONAL_PHYSICIAN_NAME = '鈴木健太';
const ORGANIZATION_NAME = 'POSIWILL合同会社';
const ORGANIZATION_ADDRESS = '〒150-0021 東京都渋谷区恵比寿西2-8-4EX恵比寿西ビル5階';

type Branch = {
  id: number;
  label: string;
};

type Props = {
  /** 対象年 */
  targetYearLabel: string;
  /** 検査実施年月 */
  inspectionYearMonthLabel: string;
  /** 検査を受けた人数 */
  inspectedCount: number | undefined;
  /** 集団分析件数 */
  groupAnalysisCount: number;
  /** 選択されている事業所 */
  selectedBranch: Branch | undefined;
  /** 事業所一覧 */
  branches: Branch[];
  /** 事業所を変更した際のハンドラー */
  onChangeBranch: (id: number) => void;
};

export const StressAssessmentReportDownloadPresentation: React.FC<Props> = ({
  targetYearLabel,
  inspectionYearMonthLabel,
  inspectedCount,
  groupAnalysisCount,
  selectedBranch,
  branches,
  onChangeBranch,
}) => (
  <div className={styles.container}>
    <h1 className={styles.pageTitle}>報告書作成ガイド</h1>

    <div className={styles.infoBanner}>
      <InfoIcon size='sm' className={styles.infoIcon} />
      <div className={styles.infoBannerContent}>
        <p>
          <a
            href='https://www.chohyo-shien.mhlw.go.jp/'
            target='_blank'
            rel='noopener noreferrer'
            className={styles.link}
          >
            厚生労働省の入力支援サービス
          </a>
          より、電子申請または従来方式（pdfファイルを印刷・提出）をご選択ください。
        </p>
        <p>
          以下の情報を参考に、規定の報告書に必要事項をご記入のうえ、電子申請または印刷した用紙を所轄の労働基準監督署へご提出ください。
        </p>
      </div>
    </div>

    {branches.length > 1 && (
      <div className={styles.businessLocationSelector}>
        <span className={styles.businessLocationLabel}>
          事業所
          <span className={styles.tooltipWrapper}>
            <CircleQuestionIcon className={styles.questionIcon} size='xs' />
            <span className={styles.tooltip} role='tooltip'>
              対象の事業所を選択することで、実施年や検査対象の人数などを確認できます。
            </span>
          </span>
        </span>
        <div className={styles.selectWrapper}>
          <Select
            value={selectedBranch?.id.toString()}
            onValueChange={value => onChangeBranch(Number.parseInt(value, 10))}
          >
            <SelectTrigger>
              <SelectValue
                placeholder={branches.length > 0 ? '事業所を選択' : '事業所を追加してください'}
              />
            </SelectTrigger>
            <SelectContent>
              {branches.map(option => (
                <SelectItem key={option.id} value={option.id.toString()}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    )}

    <div className={styles.contentWrapper}>
      <div className={styles.leftSection}>
        <Stack gap={24}>
          <Stack gap={8} direction='row'>
            <span className={styles.stepNumber}>1</span>
            <Stack gap={4}>
              <h3 className={styles.stepTitle}>労働保険番号を記入</h3>
              <p className={styles.stepDescription}>労働保険番号を記入してください。</p>
            </Stack>
          </Stack>

          <Stack gap={8} direction='row'>
            <span className={styles.stepNumber}>2</span>
            <Stack gap={4}>
              <h3 className={styles.stepTitle}>対象年度を記入</h3>
              <p className={styles.stepDescription}>
                対象年度：
                <Badge
                  status='info'
                  variant='subtle'
                  label={targetYearLabel}
                  size='sm'
                  showClose={false}
                />
              </p>
            </Stack>
          </Stack>

          <Stack gap={8} direction='row'>
            <span className={styles.stepNumber}>3</span>
            <Stack gap={4}>
              <h3 className={styles.stepTitle}>検査実施年月を記入</h3>
              <p className={styles.stepDescription}>
                検査実施年月：
                <Badge
                  status='info'
                  variant='subtle'
                  label={inspectionYearMonthLabel}
                  size='sm'
                  showClose={false}
                />
              </p>
            </Stack>
          </Stack>

          <Stack gap={8} direction='row'>
            <span className={styles.stepNumber}>4</span>
            <Stack gap={4}>
              <h3 className={styles.stepTitle}>事業の種類と事業場の名称を記入</h3>
              <p className={styles.stepDescription}>
                事業の種類および事業場の名称を記入してください。
              </p>
              <p className={styles.stepDescription}>
                事業の種類は
                <a
                  href='https://www.soumu.go.jp/index.html'
                  target='_blank'
                  rel='noopener noreferrer'
                  className={styles.link}
                >
                  総務省のホームページ
                </a>
                の日本標準産業分類の「中分類」を参照してください。
              </p>
            </Stack>
          </Stack>

          <Stack gap={8} direction='row'>
            <span className={styles.stepNumber}>5</span>
            <Stack gap={4}>
              <h3 className={styles.stepTitle}>事業場の所在地を記入</h3>
              {selectedBranch ? (
                <p className={styles.stepDescription}>
                  <Badge
                    status='info'
                    variant='subtle'
                    label={selectedBranch.label}
                    size='sm'
                    showClose={false}
                  />
                  の郵便番号、住所、電話番号を記入してください。
                </p>
              ) : (
                <p className={styles.stepDescription}>
                  郵便番号、住所、電話番号を記入してください。
                </p>
              )}
            </Stack>
          </Stack>

          <Stack gap={8} direction='row'>
            <span className={styles.stepNumber}>6</span>
            <Stack gap={4}>
              <h3 className={styles.stepTitle}>
                在籍労働者数を記入
                <span className={styles.tooltipWrapper}>
                  <CircleQuestionIcon className={styles.questionIcon} size='xs' />
                  <span className={styles.tooltip}>
                    在籍労働者数：期間の定めのない労働契約により使用される者（期間の定めのある労働契約により使用される者であって、当該契約の契約期間が1年以上である者並びに契約更新により1年以上使用されることが予定されている者及び1年以上引き続き使用されている者を含む。）を指します。
                  </span>
                </span>
              </h3>
              <p className={styles.stepDescription}>事業所全体の在籍労働者数を記入してください。</p>
            </Stack>
          </Stack>

          <Stack gap={8} direction='row'>
            <span className={styles.stepNumber}>7</span>
            <Stack gap={4}>
              <h3 className={styles.stepTitle}>検査の実施状況を記入</h3>
              <p className={styles.stepDescription}>
                検査実施者の情報と、検査を受けた労働者数を記入してください。
              </p>
              <p className={styles.stepDescription}>
                検査を受けた労働者数：
                <Badge
                  status='info'
                  variant='subtle'
                  label={inspectedCount?.toString() || '-'}
                  size='sm'
                  showClose={false}
                />
              </p>
            </Stack>
          </Stack>

          <Stack gap={8} direction='row'>
            <span className={styles.stepNumber}>8</span>
            <Stack gap={4}>
              <h3 className={styles.stepTitle}>面接指導の実施状況を記入</h3>
              <p className={styles.stepDescription}>
                面接指導を実施した医師の情報と、面接を受けた労働者数を記入してください。
              </p>
            </Stack>
          </Stack>

          <Stack gap={8} direction='row'>
            <span className={styles.stepNumber}>9</span>
            <Stack gap={4}>
              <h3 className={styles.stepTitle}>集団分析の実施有無を記入</h3>
              <p className={styles.stepDescription}>
                <Badge
                  status='info'
                  variant='subtle'
                  label={groupAnalysisCount.toString()}
                  size='sm'
                  showClose={false}
                />
                を記入してください（集団分析を行っていない）。
              </p>
            </Stack>
          </Stack>

          <Stack gap={8} direction='row'>
            <span className={styles.stepNumber}>10</span>
            <Stack gap={4}>
              <h3 className={styles.stepTitle}>産業医の情報を記入</h3>
              <p className={styles.stepDescription}>
                以下の産業医名をご記入ください。なお、貴社専属の産業医がいらっしゃる場合は、そちらの氏名および住所をご記載いただいても問題ございません。
              </p>
              <div className={styles.infoCard}>
                <h4 className={styles.infoCardTitle}>＜産業医の情報＞</h4>
                <div className={styles.infoCardRow}>
                  <span className={styles.infoCardLabel}>産業医の氏名</span>
                  <span className={styles.infoCardValue}>{OCCUPATIONAL_PHYSICIAN_NAME}</span>
                </div>
                <div className={styles.infoCardRow}>
                  <span className={styles.infoCardLabel}>所属機関の名称</span>
                  <span className={styles.infoCardValue}>{ORGANIZATION_NAME}</span>
                </div>
                <div className={styles.infoCardRow}>
                  <span className={styles.infoCardLabel}>所属機関の所在地</span>
                  <span className={styles.infoCardValue}>{ORGANIZATION_ADDRESS}</span>
                </div>
              </div>
            </Stack>
          </Stack>

          <Stack gap={8} direction='row'>
            <span className={styles.stepNumber}>11</span>
            <Stack gap={4}>
              <h3 className={styles.stepTitle}>提出日と事業者名を記入</h3>
              <p className={styles.stepDescription}>
                提出する年月日および事業者職氏名を記入してください。
              </p>
            </Stack>
          </Stack>

          <Stack gap={8} direction='row'>
            <span className={styles.stepNumber}>12</span>
            <Stack gap={4}>
              <h3 className={styles.stepTitle}>労働基準監督署に提出（電子申請以外の場合のみ）</h3>
              <p className={styles.stepDescription}>
                <a
                  href='https://www.mhlw.go.jp/kouseiroudoushou/shozaiannai/roudoukyoku/index.html'
                  target='_blank'
                  rel='noopener noreferrer'
                  className={styles.link}
                >
                  厚生労働省のサイト
                </a>
                より、提出先の所轄の労働基準監督署所在地をご確認のうえ提出してください。
              </p>
            </Stack>
          </Stack>
        </Stack>
      </div>

      <div className={styles.rightSection}>
        <div className={styles.templatePreview}>
          <img
            src={stressCheckReportTemplate}
            alt='心理的な負担の程度を把握するための検査結果等報告書'
            className={styles.templateImage}
          />
        </div>
      </div>
    </div>
  </div>
);
